import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Col, Row, Modal, Form } from 'react-bootstrap';
import { Card, CardBody, CardTitle } from '@spark/ui-components/components/Card';
import { Button, ButtonWithLoading } from '@spark/ui-components/components/Button';
import TanStackTable from '@spark/ui-components/components/TanStackTable/TanStackTable';
import { notification } from '@spark/ui-components/utils/notification';
import ConfirmDeleteModal from '@/containers/UI/Modals/index2';
import {
  createClienteEmails,
  deleteClienteEmail,
  getClienteById,
  getClienteEmails,
  updateClienteEmail,
} from '@spark/services/clientes';

const isValidEmail = (value) => /\S+@\S+\.\S+/.test(String(value || '').trim());

const getErrorMessage = (error, fallbackMessage) =>
  error?.response?.data?.message || error?.response?.data?.error || error?.message || fallbackMessage;

const toList = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.registros)) return value.registros;
  if (Array.isArray(value?.items)) return value.items;
  if (Array.isArray(value?.data)) return value.data;
  return [];
};

const normalizeEmailItem = (item) => ({
  id:
    item?.Id ??
    item?.id ??
    item?.ID ??
    item?.ClienteEmailId ??
    item?.clienteEmailId ??
    null,
  email: String(item?.Email ?? item?.email ?? '').trim().toLowerCase(),
  clienteId: item?.ClienteId ?? item?.clienteId ?? null,
  criadoEm: item?.CriadoEm ?? item?.criadoEm ?? null,
  atualizadoEm: item?.AtualizadoEm ?? item?.atualizadoEm ?? null,
});

const EmailsTab = ({ clienteId }) => {
  const [emails, setEmails] = useState([]);
  const [clienteNome, setClienteNome] = useState('');
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailParaExcluir, setEmailParaExcluir] = useState(null);
  const [formValues, setFormValues] = useState({
    id: null,
    email: '',
  });
  const [newEmails, setNewEmails] = useState(['']);

  const loadEmails = useCallback(async () => {
    if (!clienteId) {
      setEmails([]);
      return;
    }

    try {
      setLoading(true);

      // Fallback entre filtros em PascalCase e camelCase.
      let data;
      try {
        data = await getClienteEmails({ ClienteId: Number(clienteId) });
      } catch (firstError) {
        data = await getClienteEmails({ clienteId: Number(clienteId) });
      }

      const normalized = toList(data)
        .map(normalizeEmailItem)
        .filter((item) => item.id !== null && item.id !== undefined && item.email);

      setEmails(normalized);
    } catch (error) {
      setEmails([]);
      notification.error(getErrorMessage(error, 'Erro ao carregar emails do cliente.'));
    } finally {
      setLoading(false);
    }
  }, [clienteId]);

  useEffect(() => {
    let mounted = true;

    const loadClienteNome = async () => {
      if (!clienteId) return;

      try {
        const data = await getClienteById(clienteId);
        if (!mounted) return;
        setClienteNome(data?.Nome || data?.nome || '');
      } catch (error) {
        if (!mounted) return;
        setClienteNome('');
      }
    };

    loadClienteNome();

    return () => {
      mounted = false;
    };
  }, [clienteId]);

  useEffect(() => {
    loadEmails();
  }, [loadEmails]);

  const columns = useMemo(
    () => [
      {
        id: 'email',
        header: 'Email',
        accessorKey: 'email',
        size: 250,
        enableSorting: true,
      },
      {
        id: 'criadoEm',
        header: 'Criado em',
        accessorKey: 'criadoEm',
        size: 180,
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return '-';
          return new Date(value).toLocaleString('pt-BR');
        },
      },
      {
        id: 'atualizadoEm',
        header: 'Atualizado em',
        accessorKey: 'atualizadoEm',
        size: 180,
        enableSorting: true,
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return '-';
          return new Date(value).toLocaleString('pt-BR');
        },
      },
      {
        id: 'acoes',
        header: 'Ações',
        accessorKey: 'id',
        size: 140,
        enableSorting: false,
        cell: ({ row }) => (
          <div style={{ display: 'inline-flex', gap: 8 }}>
            <Button
              variant="outline-primary"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                const item = row.original;
                setFormValues({
                  id: item.id,
                  email: item.email,
                });
                setNewEmails(['']);
                setShowFormModal(true);
              }}
            >
              Editar
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={(event) => {
                event.stopPropagation();
                setEmailParaExcluir(row.original);
                setShowDeleteModal(true);
              }}
            >
              Excluir
            </Button>
          </div>
        ),
      },
    ],
    []
  );

  const resetForm = useCallback(() => {
    setFormValues({
      id: null,
      email: '',
    });
    setNewEmails(['']);
  }, []);

  const handleNovoEmail = useCallback(() => {
    resetForm();
    setShowFormModal(true);
  }, [resetForm]);

  const handleCloseForm = useCallback(() => {
    if (saving) return;
    setShowFormModal(false);
    resetForm();
  }, [saving, resetForm]);

  const handleSave = useCallback(async () => {
    try {
      setSaving(true);

      if (formValues.id) {
        const emailNormalizado = String(formValues.email || '').trim().toLowerCase();

        if (!emailNormalizado) {
          notification.warning('Informe um email.');
          return;
        }

        if (!isValidEmail(emailNormalizado)) {
          notification.warning('Informe um email valido.');
          return;
        }

        const jaExiste = emails.some(
          (item) => item.email.toLowerCase() === emailNormalizado && item.id !== formValues.id
        );

        if (jaExiste) {
          notification.warning('Este email ja foi cadastrado.');
          return;
        }

        await updateClienteEmail(formValues.id, {
          Email: emailNormalizado,
        });
        notification.success('Email atualizado com sucesso.');
      } else {
        const normalizedEmails = newEmails
          .map((value) => String(value || '').trim().toLowerCase())
          .filter(Boolean);

        if (!normalizedEmails.length) {
          notification.warning('Informe ao menos um email.');
          return;
        }

        if (normalizedEmails.some((value) => !isValidEmail(value))) {
          notification.warning('Um ou mais emails estao invalidos.');
          return;
        }

        const duplicatesInPayload = normalizedEmails.some(
          (value, index) => normalizedEmails.indexOf(value) !== index
        );

        if (duplicatesInPayload) {
          notification.warning('Existem emails duplicados no formulario.');
          return;
        }

        const emailJaExiste = normalizedEmails.some((value) =>
          emails.some((item) => item.email.toLowerCase() === value)
        );

        if (emailJaExiste) {
          notification.warning('Um ou mais emails ja estao cadastrados.');
          return;
        }

        await createClienteEmails({
          ClienteId: Number(clienteId),
          Emails: normalizedEmails,
        });
        notification.success(
          normalizedEmails.length > 1
            ? 'Emails cadastrados com sucesso.'
            : 'Email cadastrado com sucesso.'
        );
      }

      await loadEmails();
      setShowFormModal(false);
      resetForm();
    } catch (error) {
      notification.error(getErrorMessage(error, 'Erro ao salvar email.'));
    } finally {
      setSaving(false);
    }
  }, [clienteId, emails, formValues, loadEmails, newEmails, resetForm]);

  const handleConfirmDelete = useCallback(async () => {
    if (emailParaExcluir?.id === null || emailParaExcluir?.id === undefined) return;

    try {
      setDeleting(true);
      const idToDelete = Number(emailParaExcluir.id);
      await deleteClienteEmail(Number.isNaN(idToDelete) ? emailParaExcluir.id : idToDelete);
      await loadEmails();
      notification.success('Email removido com sucesso.');
      setShowDeleteModal(false);
      setEmailParaExcluir(null);
    } catch (error) {
      notification.error(getErrorMessage(error, 'Erro ao remover email.'));
    } finally {
      setDeleting(false);
    }
  }, [emailParaExcluir, loadEmails]);

  const handleCloseDelete = useCallback(() => {
    if (deleting) return;
    setShowDeleteModal(false);
    setEmailParaExcluir(null);
  }, [deleting]);

  return (
    <Row>
      <Col md={12}>
        <Card>
          <CardBody>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <CardTitle className="mb-0">
                Emails do Cliente{clienteNome ? ` (${clienteNome})` : ''}
              </CardTitle>
              <Button variant="primary" onClick={handleNovoEmail} disabled={loading}>
                Adicionar Email
              </Button>
            </div>

            <TanStackTable
              columns={columns}
              data={emails}
              isSortable={true}
              withPagination={true}
              pageSizeOptions={[5, 10, 25, 50]}
              enableRowColors={true}
              loading={loading}
              className="mt-2"
            />
          </CardBody>
        </Card>
      </Col>

      <Modal show={showFormModal} onHide={handleCloseForm} centered>
        <Modal.Header closeButton>
          <Modal.Title>{formValues.id ? 'Editar Email' : 'Adicionar Email'}</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {formValues.id ? (
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                value={formValues.email}
                onChange={(event) =>
                  setFormValues((prev) => ({
                    ...prev,
                    email: event.target.value,
                  }))
                }
                placeholder="email@cliente.com"
              />
            </Form.Group>
          ) : (
            <>
              {newEmails.map((value, index) => (
                <Form.Group className="mb-3" key={`email-${index}`}>
                  <Form.Label>Email {index + 1}</Form.Label>
                  <div className="d-flex gap-2">
                    <Form.Control
                      type="email"
                      value={value}
                      onChange={(event) =>
                        setNewEmails((prev) =>
                          prev.map((item, itemIndex) =>
                            itemIndex === index ? event.target.value : item
                          )
                        )
                      }
                      placeholder="email@cliente.com"
                    />
                    {newEmails.length > 1 && (
                      <Button
                        variant="outline-danger"
                        onClick={() =>
                          setNewEmails((prev) => prev.filter((_, itemIndex) => itemIndex !== index))
                        }
                      >
                        Remover
                      </Button>
                    )}
                  </div>
                </Form.Group>
              ))}

              <Button variant="outline-primary" onClick={() => setNewEmails((prev) => [...prev, ''])}>
                + Adicionar outro email
              </Button>
            </>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseForm} disabled={saving}>
            Cancelar
          </Button>
          <ButtonWithLoading variant="primary" onClick={handleSave} loading={saving}>
            Salvar
          </ButtonWithLoading>
        </Modal.Footer>
      </Modal>

      <ConfirmDeleteModal
        show={showDeleteModal}
        onClose={handleCloseDelete}
        onConfirm={handleConfirmDelete}
        loading={deleting}
        title="Excluir Email"
        message={`Tem certeza que deseja excluir o email${emailParaExcluir?.email ? ` \"${emailParaExcluir.email}\"` : ''}?`}
      />
    </Row>
  );
};

export default EmailsTab;
